// Math & Bit Manipulation — curated interview problems with multiple worked approaches.
// Same shape as dsaProblems/intervals.js: concise steps, a "why & when to use" close per
// approach, and an interactive visualizer wherever it clarifies the bit-level reasoning.

const mathBitProblems = [
  {
    id: 'single-number',
    title: 'Single Number',
    difficulty: 'Easy',
    leetcode: { number: 136, url: 'https://leetcode.com/problems/single-number/' },
    statement: 'Given a non-empty array where every element appears exactly twice except for one, find that single element. Must run in linear time with O(1) extra space.',
    example: {
      input: 'nums = [4, 1, 2, 1, 2]',
      output: '4',
      note: '1 and 2 each appear twice; 4 appears once.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (HashMap Counting)',
        why:
          "Count the occurrences of every number in a hash map, then scan for the one with a count of 1. " +
          "Correct, and works for the general case, but the O(1)-space requirement rules it out — an O(n)-space " +
          "auxiliary structure is exactly what the constraint is asking to avoid.",
        steps: [
          'Build a frequency map of every number in the array.',
          'Scan the map for the entry with count exactly 1.',
          'Return that number.',
        ],
        code:
`int singleNumberBruteForce(int[] nums) {
    Map<Integer, Integer> counts = new HashMap<>();
    for (int n : nums) counts.merge(n, 1, Integer::sum);
    for (Map.Entry<Integer, Integer> entry : counts.entrySet()) {
        if (entry.getValue() == 1) return entry.getKey();
    }
    throw new IllegalArgumentException("no single number found");
}`,
        complexity: { time: 'O(n)', space: 'O(n)' },
        whenToUse: "Correct for the general 'find the odd one out' problem, but doesn't meet this problem's explicit O(1)-space requirement — the XOR trick below is a much better fit for exactly this constraint.",
      },
      {
        name: 'Approach 2 — Optimized: XOR Everything',
        why:
          "XOR has two properties that make this trivial: x ^ x = 0 (a number cancels itself), and x ^ 0 = x " +
          "(XOR with zero is a no-op), and XOR is commutative and associative (order doesn't matter). XOR every " +
          "number in the array together, and every pair cancels out, leaving only the single unpaired number.",
        steps: [
          'Start an accumulator at 0.',
          'XOR every number in the array into the accumulator, one at a time.',
          'Every number that appears twice XORs with itself at some point, contributing nothing (x ^ x = 0).',
          'What remains is exactly the number that appeared only once.',
        ],
        visual: {
          arrays: [{ label: 'nums', values: [4, 1, 2, 1, 2] }],
          varNames: ['result'],
          steps: [
            { highlights: { nums: [0] }, vars: { result: 4 }, note: 'result = 0 ^ 4 = 4.' },
            { highlights: { nums: [1] }, vars: { result: 5 }, note: 'result = 4 ^ 1 = 5.' },
            { highlights: { nums: [2] }, vars: { result: 7 }, note: 'result = 5 ^ 2 = 7.' },
            { highlights: { nums: [3] }, vars: { result: 6 }, note: 'result = 7 ^ 1 = 6 — the first 1 XORed in has now cancelled with this second 1 (net effect: as if 1 was never there).' },
            { highlights: { nums: [4] }, vars: { result: 4 }, note: 'result = 6 ^ 2 = 4 — the two 2s have also cancelled out. Final answer: 4, exactly the number that appeared only once.' },
          ],
        },
        code:
`int singleNumber(int[] nums) {
    int result = 0;
    for (int n : nums) {
        result ^= n;
    }
    return result;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
        interviewNote: "Try the \"Single Number (XOR)\" mode in this pattern's interactive playground with any array where one value appears an odd number of times.",
        whenToUse: "The canonical XOR trick — whenever a problem involves pairs that should cancel out, and asks for O(1) space, XOR is almost always the intended tool.",
      },
    ],
  },
  {
    id: 'number-of-1-bits',
    title: 'Number of 1 Bits',
    difficulty: 'Easy',
    leetcode: { number: 191, url: 'https://leetcode.com/problems/number-of-1-bits/' },
    statement: 'Given a 32-bit unsigned integer, return the number of \'1\' bits it has (also known as the Hamming weight).',
    example: {
      input: 'n = 00000000000000000000000000001011',
      output: '3',
      note: 'Binary 1011 has three 1-bits.',
    },
    approaches: [
      {
        name: 'Approach 1 — Check Every Bit Position',
        why:
          "Shift a mask across all 32 bit positions, checking each one against the number. Correct and simple, " +
          "but always does exactly 32 iterations regardless of how many bits are actually set.",
        steps: [
          'For each of the 32 bit positions, shift n right by that many places.',
          'Mask with 1 to isolate just that bit.',
          'If it\'s 1, increment the count.',
          'Return the total count after checking all 32 positions.',
        ],
        code:
`int hammingWeightBruteForce(int n) {
    int count = 0;
    for (int i = 0; i < 32; i++) {
        if (((n >>> i) & 1) == 1) {
            count++;
        }
    }
    return count;
}`,
        complexity: { time: 'O(32) = O(1)', space: 'O(1)' },
        whenToUse: "Simple and always correct, but does fixed work regardless of the actual number of set bits — Brian Kernighan's trick below adapts to the input.",
      },
      {
        name: 'Approach 2 — Optimized: Brian Kernighan\'s Bit-Clearing Trick',
        why:
          "n & (n - 1) always clears exactly the LOWEST set bit of n, and nothing else. Repeating this clears " +
          "one set bit per iteration — so the loop runs exactly as many times as there ARE set bits, rather than " +
          "always scanning all 32 positions regardless of how sparse the bit pattern is.",
        steps: [
          'Start a counter at 0.',
          'While n is not 0: compute n & (n - 1), which clears n\'s lowest set bit.',
          'Increment the counter each time.',
          'The loop naturally ends once every set bit has been cleared — n becomes 0.',
        ],
        visual: {
          arrays: [{ label: 'n = 11 (binary 1011)', values: ['1', '0', '1', '1'] }],
          varNames: ['n (decimal)', 'count'],
          steps: [
            { vars: { 'n (decimal)': 11, count: 0 }, note: 'n = 1011 (11). n - 1 = 1010 (10). n & (n-1) = 1011 & 1010 = 1010 (10). count = 1.' },
            { vars: { 'n (decimal)': 10, count: 1 }, note: 'n = 1010 (10). n - 1 = 1001 (9). n & (n-1) = 1010 & 1001 = 1000 (8). count = 2.' },
            { vars: { 'n (decimal)': 8, count: 2 }, note: 'n = 1000 (8). n - 1 = 0111 (7). n & (n-1) = 1000 & 0111 = 0000 (0). count = 3.' },
            { vars: { 'n (decimal)': 0, count: 3 }, note: 'n is now 0 — loop ends. Final answer: 3 set bits.' },
          ],
        },
        code:
`int hammingWeight(int n) {
    int count = 0;
    while (n != 0) {
        n = n & (n - 1);
        count++;
    }
    return count;
}`,
        complexity: { time: 'O(k), k = number of set bits (at most 32)', space: 'O(1)' },
        interviewNote: "Worth explicitly naming this as \"Brian Kernighan's algorithm\" in an interview — it's a well-known trick, and citing it signals familiarity with the standard toolkit.",
        whenToUse: "Whenever a problem needs to iterate over just the SET bits of a number — this trick visits each one exactly once, doing no wasted work on the 0 bits in between.",
      },
    ],
  },
  {
    id: 'counting-bits',
    title: 'Counting Bits',
    difficulty: 'Easy',
    leetcode: { number: 338, url: 'https://leetcode.com/problems/counting-bits/' },
    statement: 'Given an integer n, return an array ans of length n + 1 where ans[i] is the number of 1\'s in the binary representation of i, for every i from 0 to n.',
    example: {
      input: 'n = 5',
      output: '[0, 1, 1, 2, 1, 2]',
      note: '0=000(0), 1=001(1), 2=010(1), 3=011(2), 4=100(1), 5=101(2).',
    },
    approaches: [
      {
        name: 'Approach 1 — Count Bits Independently for Each Number',
        why:
          "Apply a bit-counting routine (like Brian Kernighan's trick) independently to every number from 0 to " +
          "n. Correct, but ignores the fact that each number's bit count is closely related to a SMALLER number " +
          "already computed earlier in the same array.",
        steps: [
          'For each number i from 0 to n:',
          'Count its set bits independently, using any standalone bit-counting method.',
          'Store the result at ans[i].',
          'No information is shared between different values of i, even though smaller numbers were already fully processed.',
        ],
        code:
`int[] countBitsBruteForce(int n) {
    int[] ans = new int[n + 1];
    for (int i = 0; i <= n; i++) {
        int count = 0, num = i;
        while (num != 0) {
            num &= (num - 1);
            count++;
        }
        ans[i] = count;
    }
    return ans;
}`,
        complexity: { time: 'O(n log n)', space: 'O(n) for the output' },
        whenToUse: "Correct, but re-derives each count from scratch — the DP relationship below reuses previously-computed answers directly, dropping to a strict O(n).",
      },
      {
        name: 'Approach 2 — Optimized: DP Using i >> 1',
        why:
          "Every number i, when right-shifted by one bit, is exactly i with its lowest bit removed. So the set-bit " +
          "count of i equals the set-bit count of (i >> 1), plus 1 more IF i's lowest bit was itself a 1 (i & 1). " +
          "This turns the whole array into a simple bottom-up DP.",
        steps: [
          'dp[0] = 0 — zero has no set bits.',
          'For each i from 1 to n, look at i >> 1 (i with its last bit dropped) — its answer, dp[i >> 1], is already known.',
          'Add back 1 if i\'s own last bit (i & 1) was a 1.',
          'dp[i] = dp[i >> 1] + (i & 1).',
        ],
        visual: {
          arrays: [{ label: 'dp[0..5] — set-bit count for each number', values: [0] }],
          varNames: [],
          steps: [
            { snapshots: { 'dp[0..5] — set-bit count for each number': [0, 1] }, note: 'dp[1] = dp[1>>1] + (1&1) = dp[0] + 1 = 0 + 1 = 1.' },
            { snapshots: { 'dp[0..5] — set-bit count for each number': [0, 1, 1] }, note: 'dp[2] = dp[2>>1] + (2&1) = dp[1] + 0 = 1 + 0 = 1.' },
            { snapshots: { 'dp[0..5] — set-bit count for each number': [0, 1, 1, 2] }, note: 'dp[3] = dp[3>>1] + (3&1) = dp[1] + 1 = 1 + 1 = 2.' },
            { snapshots: { 'dp[0..5] — set-bit count for each number': [0, 1, 1, 2, 1] }, note: 'dp[4] = dp[4>>1] + (4&1) = dp[2] + 0 = 1 + 0 = 1.' },
            { snapshots: { 'dp[0..5] — set-bit count for each number': [0, 1, 1, 2, 1, 2] }, note: 'dp[5] = dp[5>>1] + (5&1) = dp[2] + 1 = 1 + 1 = 2. Final: [0,1,1,2,1,2] — matches directly.' },
          ],
        },
        code:
`int[] countBits(int n) {
    int[] dp = new int[n + 1];
    for (int i = 1; i <= n; i++) {
        dp[i] = dp[i >> 1] + (i & 1);
    }
    return dp;
}`,
        complexity: { time: 'O(n)', space: 'O(n) for the output' },
        interviewNote: "Try the \"Counting Bits\" mode in this pattern's interactive playground to watch dp[i >> 1] get reused for any n up to 15.",
        whenToUse: "Whenever a range of related bit-counting (or similarly recursive numeric) answers is needed all at once, look for a relationship between i and a smaller, already-computed index — here, i >> 1.",
      },
    ],
  },
  {
    id: 'missing-number',
    title: 'Missing Number',
    difficulty: 'Easy',
    leetcode: { number: 268, url: 'https://leetcode.com/problems/missing-number/' },
    statement: 'Given an array containing n distinct numbers taken from the range [0, n], find the one number in that range that is missing from the array.',
    example: {
      input: 'nums = [3, 0, 1]',
      output: '2',
      note: 'n = 3, so the full range is [0, 3] — 2 is the missing value.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Check Every Candidate)',
        why:
          "For every number from 0 to n, scan the array checking whether it's present. Correct, but each check " +
          "is a full linear scan, making the whole approach quadratic.",
        steps: [
          'For each candidate value from 0 to n:',
          'Scan the entire array checking whether that value appears.',
          'The first candidate NOT found in the array is the missing number.',
          'Every candidate check rescans the whole array from scratch.',
        ],
        code:
`int missingNumberBruteForce(int[] nums) {
    int n = nums.length;
    for (int candidate = 0; candidate <= n; candidate++) {
        boolean found = false;
        for (int x : nums) {
            if (x == candidate) { found = true; break; }
        }
        if (!found) return candidate;
    }
    return -1;
}`,
        complexity: { time: 'O(n²)', space: 'O(1) extra' },
        whenToUse: "Only useful to confirm the problem's meaning directly — both approaches below solve it in linear time without any nested scanning.",
      },
      {
        name: 'Approach 2 — Optimized: Math (Sum Formula)',
        why:
          "The sum of every integer from 0 to n has a closed-form formula: n(n+1)/2. Subtracting the ACTUAL sum " +
          "of the array from that expected sum directly reveals the one value that's missing — no scanning for " +
          "presence needed at all, just two sums and a subtraction.",
        steps: [
          'Compute expectedSum = n(n+1)/2, where n is the array\'s length (the range is [0, n]).',
          'Compute actualSum by summing every number actually in the array.',
          'The missing number is exactly expectedSum − actualSum.',
          'Return that difference.',
        ],
        visual: {
          arrays: [{ label: 'nums', values: [3, 0, 1] }],
          varNames: ['expectedSum', 'actualSum', 'missing'],
          steps: [
            { vars: { expectedSum: 6 }, note: 'n = 3. expectedSum = 3×4/2 = 6 — the sum of 0+1+2+3 if nothing were missing.' },
            { vars: { expectedSum: 6, actualSum: 4 }, note: 'actualSum = 3 + 0 + 1 = 4.' },
            { vars: { expectedSum: 6, actualSum: 4, missing: 2 }, note: 'missing = 6 − 4 = 2. Final answer: 2.' },
          ],
        },
        code:
`int missingNumber(int[] nums) {
    int n = nums.length;
    int expectedSum = n * (n + 1) / 2;
    int actualSum = 0;
    for (int x : nums) actualSum += x;
    return expectedSum - actualSum;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
        interviewNote: "Try the \"Missing Number\" mode in this pattern's interactive playground to watch the sum-formula approach run on any input.",
        whenToUse: "The most direct solution once the closed-form sum formula comes to mind — clean, and easy to explain out loud in an interview.",
      },
      {
        name: 'Approach 3 — Further Alternative: XOR',
        why:
          "The exact same cancellation trick from Single Number applies here: XOR every index from 0 to n TOGETHER " +
          "with every value actually in the array. Every number that's genuinely present cancels with its own " +
          "index pairing (in aggregate), leaving only the missing number un-cancelled.",
        steps: [
          'Start an accumulator at n (the one \"extra\" index beyond the array\'s length that has no paired value).',
          'XOR in every index from 0 to n-1, together with every value in the array, in a single pass.',
          'Every value that\'s actually present cancels against its corresponding index somewhere in the accumulation.',
          'What survives is exactly the missing number.',
        ],
        code:
`int missingNumberXOR(int[] nums) {
    int result = nums.length;
    for (int i = 0; i < nums.length; i++) {
        result ^= i ^ nums[i];
    }
    return result;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
        whenToUse: "Preferable when overflow is a genuine concern (XOR can never overflow the way a running sum theoretically could for very large inputs) — otherwise, the sum-formula approach is usually considered slightly more readable.",
      },
    ],
  },
  {
    id: 'happy-number',
    title: 'Happy Number',
    difficulty: 'Easy',
    leetcode: { number: 202, url: 'https://leetcode.com/problems/happy-number/' },
    statement: 'A number is "happy" if repeatedly replacing it with the sum of the squares of its digits eventually reaches 1. Determine whether a given number is happy.',
    example: {
      input: 'n = 19',
      output: 'true',
      note: '19 → 1²+9²=82 → 8²+2²=68 → 6²+8²=100 → 1²+0²+0²=1. Reaches 1.',
    },
    approaches: [
      {
        name: 'Approach 1 — HashSet Cycle Detection',
        why:
          "Repeatedly apply the digit-square-sum transformation, remembering every value seen so far in a " +
          "HashSet. If the sequence ever reaches 1, the number is happy; if it ever repeats a value already " +
          "seen, it's stuck in a cycle and will never reach 1.",
        steps: [
          'Track every number seen so far in a HashSet.',
          'Repeatedly replace n with the sum of the squares of its digits.',
          'Stop the moment n becomes 1 (happy) or n has already been seen before (a cycle — not happy).',
          'Return whether the loop ended because n reached 1.',
        ],
        visual: {
          arrays: [],
          varNames: [],
          steps: [
            { note: 'n=19: 1² + 9² = 1 + 81 = 82. Not seen before — continue.' },
            { note: 'n=82: 8² + 2² = 64 + 4 = 68. Not seen before — continue.' },
            { note: 'n=68: 6² + 8² = 36 + 64 = 100. Not seen before — continue.' },
            { note: 'n=100: 1² + 0² + 0² = 1. Reached 1 — happy! (For a NON-happy number like 2, the sequence instead cycles through 4,16,37,58,89,145,42,20 and back to 4, without ever reaching 1 — the HashSet catches this the moment 4 reappears.)' },
          ],
        },
        code:
`boolean isHappy(int n) {
    Set<Integer> seen = new HashSet<>();
    while (n != 1 && !seen.contains(n)) {
        seen.add(n);
        n = sumOfSquaredDigits(n);
    }
    return n == 1;
}

private int sumOfSquaredDigits(int n) {
    int sum = 0;
    while (n > 0) {
        int digit = n % 10;
        sum += digit * digit;
        n /= 10;
    }
    return sum;
}`,
        complexity: { time: 'O(log n) per transformation, bounded overall by cycle length', space: 'O(cycle length) for the HashSet' },
        interviewNote: "Try the \"Happy Number\" mode in this pattern's interactive playground with any positive number to watch the sequence and its cycle detection.",
        whenToUse: "The most intuitive approach, and perfectly fine to lead with — very easy to explain, and the O(cycle length) space is small in practice since the numbers involved shrink quickly.",
      },
      {
        name: 'Approach 2 — Optimized: Floyd\'s Cycle Detection (O(1) Space)',
        why:
          "This is structurally identical to detecting a cycle in a linked list — treat \"apply the transformation\" " +
          "as \"move to the next node.\" A slow pointer moves one step at a time; a fast pointer moves two steps at " +
          "a time. If there's a cycle, the fast pointer will eventually lap the slow one — no extra memory needed " +
          "to remember every value seen.",
        steps: [
          'Initialize slow = n, and fast = the transformation applied once to n.',
          'While fast hasn\'t reached 1 and slow hasn\'t caught up to fast: advance slow by one transformation, and fast by two.',
          'If fast reaches exactly 1, the number is happy.',
          'If instead slow and fast become equal (fast lapped slow) before reaching 1, it\'s a cycle — not happy.',
        ],
        code:
`boolean isHappy(int n) {
    int slow = n, fast = next(n);
    while (fast != 1 && slow != fast) {
        slow = next(slow);
        fast = next(next(fast));
    }
    return fast == 1;
}

private int next(int n) {
    int sum = 0;
    while (n > 0) {
        int digit = n % 10;
        sum += digit * digit;
        n /= 10;
    }
    return sum;
}`,
        complexity: { time: 'O(log n) per transformation, bounded overall by cycle length', space: 'O(1) — no HashSet needed' },
        whenToUse: "Worth mentioning as a follow-up once the HashSet solution is working — recognizing this as \"linked list cycle detection in disguise\" is a nice pattern-matching moment that shows depth beyond the first correct answer.",
      },
    ],
  },
];

export default mathBitProblems;
